import React, { useEffect, useRef, useState } from "react";

interface MintMcpTokenModalProps {
  rawToken: string;
  label: string;
  onClose: () => void;
}

// Per [[feedback-pr47-second-pass-patterns]] #23 a plaintext-secret modal must
// auto-dismiss so the value doesn't sit in the DOM after the user navigates
// away. The hygiene contract is: Escape close, backdrop close, 30 s
// auto-dismiss, clear-on-unmount, autoComplete=new-password.
const AUTO_DISMISS_MS = 30_000;

export function MintMcpTokenModal({ rawToken, label, onClose }: MintMcpTokenModalProps) {
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Mirror the secret value into a ref so we can scrub it from state on
  // unmount, but we also rely on React's normal unmount-clears-children
  // behavior to remove it from the DOM.
  const tokenRef = useRef<string>(rawToken);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    const dismissTimer = setTimeout(onClose, AUTO_DISMISS_MS);
    return () => {
      clearTimeout(dismissTimer);
      if (copyTimerRef.current !== null) {
        clearTimeout(copyTimerRef.current);
        copyTimerRef.current = null;
      }
      // Clear-on-unmount: overwrite the captured ref so it isn't retained
      // by any closure that survives this modal's lifetime.
      tokenRef.current = "";
    };
  }, [onClose]);

  function handleCopy() {
    navigator.clipboard
      .writeText(rawToken)
      .then(() => {
        setCopied(true);
        if (copyTimerRef.current !== null) clearTimeout(copyTimerRef.current);
        copyTimerRef.current = setTimeout(() => {
          setCopied(false);
          copyTimerRef.current = null;
        }, 2000);
      })
      .catch(() => {
        setCopied(false);
      });
  }

  function handleBackdropClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <div
      className="sr-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={`MCP token: ${label}`}
      onClick={handleBackdropClick}
    >
      <div className="sr-modal">
        <div className="sr-modal-header">
          <h2 className="sr-modal-title">MCP token: {label}</h2>
          <button className="sr-modal-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="sr-modal-body">
          <p className="sr-modal-label">
            This token is shown once. Paste it into <code>agentguard install-mcp</code> on machines
            that need it. We cannot recover it later — re-mint if lost.
          </p>
          <div className="ac-vault-reveal-display">{rawToken}</div>
          {/*
            autoComplete=new-password on the readonly input ensures browser
            password managers do not try to "save" a one-time bearer token.
          */}
          <input
            type="text"
            readOnly
            value={rawToken}
            autoComplete="new-password"
            aria-hidden="true"
            tabIndex={-1}
            style={{ position: "absolute", left: "-10000px" }}
          />
          <div className="sr-modal-actions">
            <button
              type="button"
              className="sr-modal-btn sr-modal-btn--secondary"
              onClick={handleCopy}
            >
              {copied ? "Copied!" : "Copy"}
            </button>
            <button type="button" className="sr-modal-btn sr-modal-btn--primary" onClick={onClose}>
              I've saved it — close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
